// =====================================================================
// Aba 1 — Identidade Visual (Fase 3, S38).
// Edita campos basicos do clinic_config que afetam branding+contato:
//   clinic_name, lead_professional_name/specialty/crm, primary_color,
//   logo_url, clinic_phone.
//
// Backend (n8n Admin v2):
//   GET /v2/admin/config?slug=xxx     → carrega config completa
//   PUT /v2/admin/config?slug=xxx     → atualiza so os campos enviados
//     (whitelist + COALESCE no UPDATE; veja Build PUT Payload no workflow)
//
// IMPORTANTE:
//   - jwt_secret/company_id/slug NUNCA mandados no body (sao protegidos)
//   - primary_color obrigatorio formato #RRGGBB (validado server+client)
//   - PUT manda APENAS campos editados (dirtyFields) pra nao sobrescrever
//     campos nao-mostrados nesta aba (ex: working_hours_start, features_enabled)
// =====================================================================
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Grid,
  TextField,
  Button,
  Box,
  Typography,
  CircularProgress,
  InputAdornment,
  IconButton,
  Avatar,
  Divider
} from "@material-ui/core";
import SaveIcon from "@material-ui/icons/Save";
import RefreshIcon from "@material-ui/icons/Refresh";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import { toast } from "react-toastify";

import {
  getClinicConfig,
  updateClinicConfig,
  uploadClinicLogo
} from "../../../services/clinicAdminService";

// Validacoes client-side do upload de logo (espelham o backend)
const LOGO_MAX_BYTES = 2 * 1024 * 1024; // 2 MB
const LOGO_MIMETYPES = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/svg+xml",
  "image/webp"
];

const useStyles = makeStyles((theme) => ({
  root: { width: "100%" },
  sectionTitle: {
    fontWeight: 700,
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    marginTop: theme.spacing(2.5),
    marginBottom: theme.spacing(1.25)
  },
  brandingRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    flexWrap: "wrap"
  },
  logoPreview: {
    width: 72,
    height: 72,
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.default,
    objectFit: "contain",
    flexShrink: 0
  },
  logoPreviewEmpty: {
    width: 72,
    height: 72,
    borderRadius: 12,
    border: `1px dashed ${theme.palette.divider}`,
    background: theme.palette.background.default,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: theme.palette.text.disabled,
    fontSize: "0.75rem",
    flexShrink: 0
  },
  colorRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    flexWrap: "wrap"
  },
  colorSwatch: {
    width: 44,
    height: 44,
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    flexShrink: 0,
    boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.06)"
  },
  // input type=color nativo, escondido visualmente mas funcional
  colorPicker: {
    width: 44,
    height: 44,
    padding: 0,
    border: "none",
    background: "transparent",
    cursor: "pointer"
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    marginTop: theme.spacing(3)
  },
  loadingWrap: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(6)
  },
  hint: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    marginTop: 4
  }
}));

// Campos que esta aba edita. Outras abas editam outros — manter
// disjunto pra que PUT delta nao sobrescreva.
const EDITABLE_FIELDS = [
  "clinic_name",
  "lead_professional_name",
  "lead_professional_specialty",
  "lead_professional_crm",
  "primary_color",
  "logo_url",
  "clinic_phone"
];

const COLOR_REGEX = /^#[0-9a-fA-F]{6}$/;

const IdentidadeVisual = ({ slug }) => {
  const classes = useStyles();

  const [config, setConfig] = useState(null);
  const [original, setOriginal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = React.useRef(null);

  const load = useCallback(async () => {
    if (!slug) return;
    try {
      setLoading(true);
      const data = await getClinicConfig(slug);
      // Endpoint retorna { config } ou direto o objeto, dependendo do node.
      const cfg = data && data.config ? data.config : data;
      setConfig(cfg || {});
      setOriginal(cfg || {});
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        "Erro carregando configuração";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    load();
  }, [load]);

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setConfig((prev) => ({ ...(prev || {}), [field]: value }));
  };

  // Diff: campos editaveis que mudaram. Evita sobrescrever campos
  // nao-mostrados nesta aba com os valores que vieram do GET inicial.
  const dirtyFields = useMemo(() => {
    if (!config || !original) return {};
    const out = {};
    EDITABLE_FIELDS.forEach((k) => {
      const before = original[k] ?? null;
      const after = config[k] ?? null;
      if (String(before ?? "") !== String(after ?? "")) {
        out[k] = after;
      }
    });
    return out;
  }, [config, original]);

  const isDirty = Object.keys(dirtyFields).length > 0;

  const validate = () => {
    if (dirtyFields.primary_color !== undefined) {
      if (!COLOR_REGEX.test(dirtyFields.primary_color)) {
        toast.error("Cor primária inválida. Use formato #RRGGBB (ex: #7c3aed)");
        return false;
      }
    }
    if (
      dirtyFields.clinic_name !== undefined &&
      !String(dirtyFields.clinic_name).trim()
    ) {
      toast.error("Nome da clínica não pode ficar vazio");
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!isDirty) {
      toast.info("Nada pra salvar");
      return;
    }
    if (!validate()) return;
    try {
      setSaving(true);
      await updateClinicConfig(slug, dirtyFields);
      toast.success("Identidade visual atualizada");
      // Reload pra pegar updated_at + valores normalizados pelo backend
      await load();
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        "Erro salvando configuração";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setConfig(original);
  };

  // Upload de logo — preenche o campo logo_url automaticamente.
  // User precisa clicar Salvar depois pra persistir no DB (mesmo padrao
  // dos outros campos da aba). Validacoes client + server.
  const handleUploadLogo = async (event) => {
    const file = event.target.files?.[0];
    // Reset do input pra permitir re-upload do mesmo arquivo
    if (event.target) event.target.value = "";
    if (!file) return;

    // Validacoes client
    if (!LOGO_MIMETYPES.includes(file.type)) {
      toast.error(`Tipo inválido: ${file.type || "desconhecido"}. Use PNG, JPG, SVG ou WEBP.`);
      return;
    }
    if (file.size > LOGO_MAX_BYTES) {
      toast.error(`Arquivo muito grande (${(file.size / 1024 / 1024).toFixed(1)} MB). Máximo 2 MB.`);
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);
      const result = await uploadClinicLogo(slug, file, (pct) => {
        setUploadProgress(pct);
      });
      // result = { success, url, filename, size, mimetype, slug }
      setConfig((prev) => ({ ...(prev || {}), logo_url: result.url }));
      toast.success(`Logo enviado (${(result.size / 1024).toFixed(0)} KB). Clique Salvar para aplicar.`);
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        "Erro no upload do logo";
      toast.error(msg);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleClickUpload = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  if (loading) {
    return (
      <div className={classes.loadingWrap}>
        <CircularProgress />
      </div>
    );
  }

  if (!config) {
    return (
      <Typography color="error" style={{ padding: 16 }}>
        Não foi possível carregar a configuração.
      </Typography>
    );
  }

  const cfg = config;
  const primaryColorValue = cfg.primary_color || "#000000";

  return (
    <div className={classes.root}>
      <Typography className={classes.sectionTitle}>
        Marca da clínica
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            label="Nome da clínica"
            variant="outlined"
            fullWidth
            size="small"
            value={cfg.clinic_name || ""}
            onChange={handleChange("clinic_name")}
            helperText="Aparece no login e no cabeçalho da agenda"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Telefone da clínica"
            variant="outlined"
            fullWidth
            size="small"
            value={cfg.clinic_phone || ""}
            onChange={handleChange("clinic_phone")}
            placeholder="(81) 99999-9999"
          />
        </Grid>
      </Grid>

      <Typography className={classes.sectionTitle}>Logotipo</Typography>

      <Box className={classes.brandingRow}>
        {cfg.logo_url ? (
          <img
            src={cfg.logo_url}
            alt="Logo da clínica"
            className={classes.logoPreview}
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        ) : (
          <div className={classes.logoPreviewEmpty}>sem logo</div>
        )}
        <Box style={{ flexGrow: 1, minWidth: 130, display: "flex", flexDirection: "column", gap: 8 }}>
          <TextField
            label="URL do logotipo"
            variant="outlined"
            fullWidth
            size="small"
            value={cfg.logo_url || ""}
            onChange={handleChange("logo_url")}
            placeholder="https://api.clinicaclee.com/public/brand/clee-12345.png"
            helperText={
              uploading
                ? `Enviando… ${uploadProgress}%`
                : "Clique em Upload pra subir um arquivo, OU cole uma URL pública."
            }
          />
          <Box style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
              style={{ display: "none" }}
              onChange={handleUploadLogo}
            />
            <Button
              variant="outlined"
              size="small"
              startIcon={
                uploading ? (
                  <CircularProgress size={16} />
                ) : (
                  <CloudUploadIcon fontSize="small" />
                )
              }
              onClick={handleClickUpload}
              disabled={uploading}
            >
              {uploading ? `Enviando ${uploadProgress}%` : "Upload"}
            </Button>
            <Typography variant="caption" style={{ color: "#888" }}>
              PNG, JPG, SVG ou WEBP — máx 2 MB
            </Typography>
          </Box>
        </Box>
      </Box>

      <Typography className={classes.sectionTitle}>Cor primária</Typography>

      <Box className={classes.colorRow}>
        <input
          type="color"
          className={classes.colorPicker}
          value={COLOR_REGEX.test(primaryColorValue) ? primaryColorValue : "#000000"}
          onChange={handleChange("primary_color")}
          aria-label="Seletor de cor primária"
        />
        <TextField
          label="Hex"
          variant="outlined"
          size="small"
          value={cfg.primary_color || ""}
          onChange={handleChange("primary_color")}
          placeholder="#7c3aed"
          style={{ flexGrow: 1, minWidth: 130 }}
          helperText="Formato #RRGGBB"
          error={
            !!cfg.primary_color && !COLOR_REGEX.test(cfg.primary_color)
          }
        />
        <Typography className={classes.hint}>
          Aplicada ao tema da agenda do paciente (botões, destaques, header).
        </Typography>
      </Box>

      <Divider style={{ margin: "24px 0" }} />

      <Typography className={classes.sectionTitle}>
        Profissional principal (cabeçalho)
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            label="Nome do profissional"
            variant="outlined"
            fullWidth
            size="small"
            value={cfg.lead_professional_name || ""}
            onChange={handleChange("lead_professional_name")}
            helperText="Aparece como subtítulo da agenda"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            label="CRM"
            variant="outlined"
            fullWidth
            size="small"
            value={cfg.lead_professional_crm || ""}
            onChange={handleChange("lead_professional_crm")}
            placeholder="CRM-PE 12345"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            label="Especialidade"
            variant="outlined"
            fullWidth
            size="small"
            value={cfg.lead_professional_specialty || ""}
            onChange={handleChange("lead_professional_specialty")}
            placeholder="Ginecologia"
          />
        </Grid>
      </Grid>

      <Box className={classes.actions}>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleReset}
          disabled={!isDirty || saving}
        >
          Descartar
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={
            saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />
          }
          onClick={handleSave}
          disabled={!isDirty || saving}
        >
          {saving ? "Salvando…" : `Salvar${isDirty ? ` (${Object.keys(dirtyFields).length})` : ""}`}
        </Button>
      </Box>
    </div>
  );
};

export default IdentidadeVisual;
